import {
    GetStaticPathsResult,
    GetStaticPathsContext,
    GetStaticProps,
} from "next";
import { ClinicPageLayout } from "layouts/relaunch/pages/ClinicPageLayout";
import { axiosCmsInstance } from "api/global";

import { serverSideTranslations } from "utils/next/translations";
import {
    UTSLocaleToLanguage,
    Theme,
    I18nLocale,
    I18nLocaleToUTSLocale,
} from "constants/enum";
import { ClinicPageEntity } from "types/clinic";
import { getUTSLocale } from "utils/locale";
import { getAppMode } from "utils/getAppMode";

interface ClinicPageProps {
    clinic: ClinicPageEntity;
}

type ClinicParams = {
    id: string;
};

const { isDevMode, isPreliveMode } = getAppMode();

export default function ClinicPage({ clinic }: ClinicPageProps) {
    return <ClinicPageLayout content={clinic} />;
}

export async function getStaticPaths({
    locales,
}: GetStaticPathsContext): Promise<GetStaticPathsResult<ClinicParams>> {
    const paths: Array<string | { params: ClinicParams; locale?: string }> = [];

    if (isDevMode || isPreliveMode) {
        return {
            paths: [],
            fallback: "blocking",
        };
    }

    for (const locale of locales as I18nLocale[]) {
        const utsLocale = I18nLocaleToUTSLocale[locale];
        // Filter locales from Next 'default' locale
        if (!utsLocale) continue;

        const { data } = await axiosCmsInstance.get<ClinicPageEntity[]>(
            `/clinic-pages`,
            {
                params: {
                    _limit: 1000,
                    "language.code": UTSLocaleToLanguage[utsLocale],
                },
            }
        );

        paths.push(
            ...data.map((clinic) => ({
                params: { id: clinic.clinic.clinicId },
                locale,
            }))
        );
    }

    return { paths, fallback: false };
}

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
    const {
        data: [clinic],
    } = await axiosCmsInstance.get<ClinicPageEntity[]>(`/clinic-pages`, {
        params: {
            "clinic.clinicId": params?.id,
            "language.code": UTSLocaleToLanguage[getUTSLocale(locale)],
        },
    });
    const { seoTitle, seoDescription } = clinic;

    return {
        props: {
            clinic,
            seoTitle,
            seoDescription,
            ...(await serverSideTranslations(locale, [
                "navigationMenuRelaunch",
                "map",
            ])),
            themeType: Theme.SYT,
        },
    };
};
